/**
 * Комиссия Polymarket — зеркало `services/internal/fees`.
 *
 * Формула ВЫВЕДЕНА ИЗМЕРЕНИЕМ, а не взята из документации биржи (там их две, и
 * они расходятся). Сверка с фактическим списанием на цепочке по двенадцати
 * реальным сделкам дала совпадение до микро-единицы:
 *
 *     комиссия = floor₅(размер × rate × p × (1 − p))
 *
 * Четыре решения в этой строке, и каждое проверено:
 *
 *  1. `p × (1 − p)`, а не `min(p, 1−p)`: на цене 0.8 они расходятся в полтора
 *     раза, и сошёлся первый;
 *  2. усечение до ПЯТИ знаков, а не округление: 29.41176 share по 0.85 дают
 *     0.2624999580, а биржа списала 0.26249;
 *  3. платит только тейкер — у мейкерских филлов на цепочке комиссия ноль;
 *  4. комиссия в USDC и у покупателя СВЕРХ нотионала: «$5» стоят $5.28.
 *
 * Почему это важно оператору числом, а не процентом: доля комиссии в сумме равна
 * `rate × (1 − p)` и уезжает от 1.4 % на дорогих исходах до 6.3 % на дешёвых.
 * Единственные осмысленные единицы — доллары и центы на share; у p ≈ 0.5 это
 * 1.75 ¢, то есть почти два тика.
 *
 * Тесты обоих зеркал читают ОДИН файл — `services/testdata/fees/cases.json`, —
 * поэтому расхождение с Go становится падающим тестом, а не сюрпризом на деньгах.
 */

import type { FeeSchedule } from "../api/types";
import { parse, SCALE, type Micro } from "./money";

/**
 * Шаг, до которого биржа усекает комиссию: 0.00001 USDC.
 *
 * Пять знаков против шести у Micro, поэтому отдельная константа: комиссия
 * 0.2624999 списывается как 0.26249, и шестого знака в ней быть не может.
 */
export const PRECISION = 10;

/** Расписание в микро-единицах, разобранное один раз. */
interface Parsed {
  rate: Micro;
  rebateRate: Micro;
}

/** crypto_fees_v2 — единственное расписание, на котором формула проверена. */
export const TYPE_CRYPTO_V2 = "crypto_fees_v2";

/**
 * Умеем ли мы считать комиссию по этому расписанию.
 *
 * Незнакомое расписание обязано ВЫКЛЮЧАТЬ число, а не подставлять ноль: ноль на
 * экране неотличим от «комиссии нет», и оператор принял бы решение по цифре,
 * которой никто не проверял. Отсюда null во всех функциях ниже.
 */
export function known(s: FeeSchedule | undefined): boolean {
  return parsed(s) !== null;
}

/**
 * Рынок комиссии НЕ БЕРЁТ — и это знание, а не его отсутствие.
 *
 * Зеркало `fees.Schedule.Free`. Отдельно от `known`, потому что ответы разные: там
 * число считается формулой, здесь оно равно нулю по условию рынка, а у неизвестного
 * рынка числа нет вовсе. Свести второе с третьим значило бы показать прочерк там,
 * где ответ известен точно.
 */
export function free(s: FeeSchedule | undefined): boolean {
  return s !== undefined && s.synced === true && !s.enabled;
}

function parsed(s: FeeSchedule | undefined): Parsed | null {
  if (s === undefined || !s.enabled || !s.taker_only) return null;
  // Тип сверяется наравне со ставкой: формула выведена измерением на
  // crypto_fees_v2, и расписание с другим feeType — это ДРУГАЯ формула биржи.
  // Считать по ней нашей значило бы показать оператору непроверенное число.
  if (s.type !== TYPE_CRYPTO_V2) return null;
  // Показатель видели только единицей; всё прочее выключает расчёт, потому что
  // роль показателя проверить было не на чем.
  if (s.exponent !== 1) return null;
  const rate = parse(s.rate ?? "");
  if (rate === null || rate <= 0) return null;
  return { rate, rebateRate: parse(s.rebate_rate ?? "") ?? 0 };
}

/**
 * Комиссия тейкера при сделке размера `size` по цене `price`, в микро-единицах.
 *
 * Цена одна, поэтому функция годится для ОЦЕНКИ — для кнопки, где известен
 * уровень стакана. Биржа считает составной ордер по эффективной цене всего
 * ордера, и расхождение с фактом ограничено `rate × Δp`: при Δp в один тик это
 * 0.0007 USD на share, то есть 7 % тика (граница закреплена тестом в Go).
 *
 * null — расписание незнакомо или величины невозможны. Ноль при этом законен:
 * у цены на самом краю комиссия меньше 0.00001 и усекается.
 */
export function takerFee(
  schedule: FeeSchedule | undefined,
  price: Micro,
  size: Micro,
): Micro | null {
  if (price <= 0 || price >= SCALE || size <= 0) return null;
  // Рынок без комиссии отвечает нулём, а не прочерком: это ответ, а не незнание.
  if (free(schedule)) return 0;
  const p = parsed(schedule);
  if (p === null) return null;
  // BigInt, а не арифметика double: произведение четырёх величин масштаба 1e6
  // доходит до 4·10²³ и выходит за 2⁵³ на первом же реальном ордере.
  const num = BigInt(size) * BigInt(p.rate) * BigInt(price) * BigInt(SCALE - price);
  const den = BigInt(SCALE) * BigInt(SCALE) * BigInt(SCALE);
  return floorToPrecision(num / den);
}

/**
 * Комиссия тейкера, приходящаяся на ОДИН share при цене `price`.
 *
 * Это ставка, а не списание, поэтому усечения до пяти знаков здесь нет: величина
 * нужна для порогов («покупать имеет смысл дешевле, чем справедливая цена минус
 * это»), и обрезать её до шага списания значило бы врать в третьем знаке цены.
 */
export function feePerShare(
  schedule: FeeSchedule | undefined,
  price: Micro,
): Micro | null {
  if (price <= 0 || price >= SCALE) return null;
  if (free(schedule)) return 0;
  const p = parsed(schedule);
  if (p === null) return null;
  const num = BigInt(p.rate) * BigInt(price) * BigInt(SCALE - price);
  return toMicro(num / (BigInt(SCALE) * BigInt(SCALE)));
}

function floorToPrecision(v: bigint): Micro | null {
  const micro = toMicro(v);
  if (micro === null) return null;
  // Величина неотрицательна, поэтому усечение вниз — это просто отбрасывание
  // остатка.
  return micro - (micro % PRECISION);
}

function toMicro(v: bigint): Micro | null {
  if (v < 0n || v > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(v);
}
